import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Animated, AppState, Image, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { Stack, useFocusEffect, useLocalSearchParams } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { StyleSheet, UnistylesRuntime, useUnistyles, withUnistyles } from 'react-native-unistyles';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import useAuthStore from '@/data/store/useAuthStore';
import { Query } from '@/utils/react-query/Queries';

const UnistylesLinearGradient = withUnistyles(LinearGradient);

const JTR_LOGO_LIGHT = require('@/assets/JTR/jtr_white.jpg');
const JTR_LOGO_DARK = require('@/assets/JTR/jtr_black.webp');
const CHURCH_LOGO_LIGHT = { uri: 'https://mlmibpzqkvhttynlzgpw.supabase.co/storage/v1/object/public/church-app-pub-files/org_logos/1a872f6d-73d9-4d8f-a458-1c8800a7b621/logo-long-dark.png' };
const CHURCH_LOGO_DARK = { uri: 'https://mlmibpzqkvhttynlzgpw.supabase.co/storage/v1/object/public/church-app-pub-files/org_logos/1a872f6d-73d9-4d8f-a458-1c8800a7b621/logo-long.png' };

const isTruthyTvMode = (value) => value !== undefined && value !== null && value !== 'false' && value !== '0';

const OVERALL_KEY = 'overall';
const POLL_INTERVAL = 30000;
const JTR_PALETTE = {
    light: { accent: '#8A5A00', container: '#FFF2C7', onAccent: '#FFFFFF' },
    dark: { accent: '#F9C555', container: '#30260F', onAccent: '#241A00' },
};

const getJtrPalette = (scheme) => JTR_PALETTE[scheme === 'dark' ? 'dark' : 'light'];

const METRIC_GROUPS = [
    {
        title: 'Attendance breakdown',
        metrics: [
            { key: 'uniqueAttendance', label: 'Unique Attendance', icon: 'finger-print-outline', overallOnly: true },
            { key: 'adultsAttending', label: 'Adults Attending', icon: 'person-outline' },
            { key: 'kidsAttending', label: 'Kids Attending', icon: 'happy-outline' },
        ],
    },
    {
        title: 'Response',
        metrics: [
            { key: 'spiritBaptism', label: 'Spirit Baptism', icon: 'flame-outline' },
            { key: 'waterBaptism', label: 'Water Baptism', icon: 'water-outline' },
        ],
    },
    {
        title: 'Member Status',
        metrics: [
            { key: 'guests', label: 'Guests', icon: 'person-add-outline' },
            { key: 'hostChurch', label: 'Host Church', icon: 'home-outline' },
            { key: 'anotherApostolicChurch', label: 'Another Apostolic Church', icon: 'church-outline', library: 'material' },
        ],
    },
];

const getCurrentDateKey = (timezone) => {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(new Date());
        const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
        return `${values.year}-${values.month}-${values.day}`;
    } catch {
        return null;
    }
};

const getInitialSelection = (data) => {
    if (!data) return OVERALL_KEY;
    const currentDateKey = getCurrentDateKey(data.event.timezone);
    return data.dates.some((date) => date.key === currentDateKey) ? currentDateKey : OVERALL_KEY;
};

const getTvSelection = (data) => {
    if (!data?.dates?.length) return OVERALL_KEY;
    const currentDateKey = getCurrentDateKey(data.event.timezone);
    if (!currentDateKey || currentDateKey > data.dates[data.dates.length - 1].key) return OVERALL_KEY;
    return data.dates.find((date) => date.key >= currentDateKey)?.key || OVERALL_KEY;
};

const formatUpdatedAt = (updatedAt, timezone) => {
    if (!updatedAt) return 'Live totals';
    try {
        return `Updated ${new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            hour: 'numeric',
            minute: '2-digit',
            timeZoneName: 'short',
        }).format(new Date(updatedAt))}`;
    } catch {
        return 'Live totals';
    }
};

const JtrIcon = (props) => {
    const { theme: { scheme } } = useUnistyles();
    return <Unicon {...props} color={getJtrPalette(scheme).accent} />;
};

const MetricRow = ({ metric, value, isLast }) => (
    <View style={[styles.metricRow, !isLast && styles.metricRowDivider]} testID={`event-stats-${metric.key}`}>
        <View style={styles.metricIdentity}>
            <View style={styles.metricIcon}>
                <JtrIcon name={metric.icon} library={metric.library || 'ionicon'} size={18} />
            </View>
            <Text style={styles.metricLabel}>{metric.label}</Text>
        </View>
        <Text style={styles.metricValue}>{new Intl.NumberFormat('en-US').format(value ?? 0)}</Text>
    </View>
);

const LoadingState = ({ color }) => (
    <View style={styles.stateCard} testID="event-stats-loading" accessibilityLiveRegion="polite">
        <ActivityIndicator color={color} size="small" />
        <Text style={styles.stateTitle}>Gathering live totals</Text>
        <Text style={styles.stateCopy}>Revival attendance will appear here in a moment.</Text>
    </View>
);

const ErrorState = ({ onRetry, isRetrying }) => (
    <View style={styles.stateCard} testID="event-stats-error" accessibilityRole="alert">
        <View style={styles.errorIcon}>
            <JtrIcon name="cloud-offline-outline" size={24} />
        </View>
        <Text style={styles.stateTitle}>Live totals are unavailable</Text>
        <Text style={styles.stateCopy}>Check your connection, then try loading the revival report again.</Text>
        <SolidButton
            text="Try again"
            type="events"
            onPress={onRetry}
            processing={isRetrying}
            testID="event-stats-retry"
        />
    </View>
);

const LiveDot = ({ color }) => {
    const opacity = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        const loop = Animated.loop(
            Animated.sequence([
                Animated.timing(opacity, {
                    toValue: 0.25,
                    duration: 1400,
                    useNativeDriver: Platform.OS !== 'web',
                }),
                Animated.timing(opacity, {
                    toValue: 1,
                    duration: 1400,
                    useNativeDriver: Platform.OS !== 'web',
                }),
            ]),
        );
        loop.start();
        return () => loop.stop();
    }, [opacity]);

    return (
        <Animated.View
            style={[styles.liveDot, { backgroundColor: color, opacity }]}
            testID="event-stats-live-dot"
        />
    );
};

const TvStatCard = ({ testID, icon, label, value, accentColor, compact = false }) => {
    if (compact) {
        return (
            <View style={styles.tvOverallCard} testID={testID}>
                <View style={styles.tvStatHeading}>
                    <View style={styles.tvOverallIcon}>
                        <Unicon name={icon} size={24} color={accentColor} />
                    </View>
                    <Text style={styles.tvOverallLabel}>{label}</Text>
                </View>
                <Text style={styles.tvOverallValue}>
                    {new Intl.NumberFormat('en-US').format(value ?? 0)}
                </Text>
            </View>
        );
    }
    return (
        <View style={styles.tvTodayCard} testID={testID}>
            <Text style={styles.tvTodayLabel}>{label}</Text>
            <Text style={styles.tvTodayValue}>
                {new Intl.NumberFormat('en-US').format(value ?? 0)}
            </Text>
        </View>
    );
};

const TvMode = ({ data, query, effectiveScheme, toggleTheme, todayLabel, todayStats, overallStats, showingOverall, insets }) => {
    const { theme: { colors } } = useUnistyles();
    const isDark = effectiveScheme === 'dark';
    const jtrPalette = getJtrPalette(effectiveScheme);
    const logoSource = isDark ? JTR_LOGO_DARK : JTR_LOGO_LIGHT;
    const churchLogoSource = isDark ? CHURCH_LOGO_DARK : CHURCH_LOGO_LIGHT;
    const statusText = query.isFetching
        ? 'Refreshing'
        : query.isRefetchError
            ? 'Live update delayed'
            : formatUpdatedAt(data?.updatedAt, data?.event.timezone);

    return (
        <View style={styles.tvPage(isDark)} testID="event-stats-tv-page">
            <Stack.Screen options={{ headerShown: false, title: data?.event.name || 'Jesus Tent Revival' }} />
            <View style={styles.tvContent(insets.top, insets.bottom)}>
                <Pressable
                    onPress={toggleTheme}
                    style={({ pressed }) => [styles.tvAction, pressed && styles.tvActionPressed]}
                    accessibilityRole="button"
                    accessibilityLabel={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                    testID="event-stats-theme-toggle"
                >
                    <Unicon
                        name={isDark ? 'sunny-outline' : 'moon-outline'}
                        size={22}
                        color={jtrPalette.accent}
                    />
                </Pressable>

                <View style={styles.tvBrandPanel}>
                    <Image source={logoSource} style={styles.tvLogo} resizeMode="contain" testID="event-stats-tv-logo" />
                    <View style={styles.tvBrandFooter}>
                        <Image
                            source={churchLogoSource}
                            style={styles.tvChurchLogo}
                            resizeMode="contain"
                            accessibilityLabel={`Calvary Church logo, ${isDark ? 'dark' : 'light'} mode`}
                            testID="event-stats-tv-church-logo"
                        />
                        <View style={styles.tvDateRule} />
                        <Text style={styles.tvEventDates}>SEPTEMBER 11—13</Text>
                        <View
                            style={[styles.tvLiveStatus, query.isRefetchError && styles.tvLiveStatusWarning]}
                            accessibilityLiveRegion="polite"
                            testID="event-stats-live-status"
                        >
                            {query.isFetching ? (
                                <ActivityIndicator color={jtrPalette.accent} size="small" />
                            ) : (
                                <LiveDot color={query.isRefetchError ? colors.warning : colors.success} />
                            )}
                            <Text style={[styles.tvLiveStatusText, query.isRefetchError && styles.tvLiveStatusTextWarning]}>
                                {statusText}
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={styles.tvScoreboard}>
                    <View style={styles.tvSectionHeader}>
                        <Text style={styles.tvSectionEyebrow}>EVENT TOTAL</Text>
                        <Text style={styles.tvSectionTitle} testID="event-stats-tv-today-label">Sept 11–13</Text>
                    </View>
                    <View style={styles.tvTodayRow}>
                        <TvStatCard
                            testID="event-stats-tv-overall-spiritBaptism"
                            label="SPIRIT BAPTISMS"
                            value={overallStats?.spiritBaptism}
                            accentColor={jtrPalette.accent}
                        />
                        <TvStatCard
                            testID="event-stats-tv-overall-waterBaptism"
                            label="WATER BAPTISMS"
                            value={overallStats?.waterBaptism}
                            accentColor={jtrPalette.accent}
                        />
                    </View>

                    <View style={styles.tvOverallSection}>
                        <Text style={styles.tvOverallEyebrow} testID="event-stats-tv-tonight-label">
                            {showingOverall ? 'ALL THREE NIGHTS' : `TONIGHT — ${todayLabel}`}
                        </Text>
                        <View style={styles.tvOverallRow}>
                            <TvStatCard
                                compact
                                testID="event-stats-tv-spiritBaptism"
                                icon="flame-outline"
                                label="SPIRIT BAPTISMS"
                                value={todayStats?.spiritBaptism}
                                accentColor={jtrPalette.accent}
                            />
                            <TvStatCard
                                compact
                                testID="event-stats-tv-waterBaptism"
                                icon="water-outline"
                                label="WATER BAPTISMS"
                                value={todayStats?.waterBaptism}
                                accentColor={jtrPalette.accent}
                            />
                        </View>
                    </View>
                </View>
            </View>
        </View>
    );
};

const JesusTentRevivalStatsPage = () => {
    const insets = useSafeAreaInsets();
    const { theme: { colors, scheme } } = useUnistyles();
    const authTheme = useAuthStore((s) => s.theme);
    const setAuthTheme = useAuthStore((s) => s.setTheme);
    const [selectedKey, setSelectedKey] = useState(null);
    const [screenFocused, setScreenFocused] = useState(true);
    const [appActive, setAppActive] = useState(AppState.currentState !== 'background' && AppState.currentState !== 'inactive');
    const appActiveRef = useRef(appActive);
    const hasFocusedRef = useRef(false);
    const searchParams = useLocalSearchParams();
    const isTvMode = isTruthyTvMode(searchParams.tv_mode);

    const query = Query.events.publicJesusTentRevivalStats({
        options: {
            refetchInterval: screenFocused && appActive ? POLL_INTERVAL : false,
            refetchIntervalInBackground: false,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
        },
    });

    const { data, refetch } = query;

    const effectiveScheme = authTheme === 'light' || authTheme === 'dark' ? authTheme : (scheme || 'light');
    const jtrPalette = getJtrPalette(effectiveScheme);
    const toggleTheme = useCallback(() => {
        const next = effectiveScheme === 'dark' ? 'light' : 'dark';
        UnistylesRuntime.setAdaptiveThemes(false);
        UnistylesRuntime.setTheme(next);
        setAuthTheme(next);
    }, [effectiveScheme, setAuthTheme]);

    const handleRefresh = useCallback(() => {
        if (!query.isFetching) refetch();
    }, [query.isFetching, refetch]);

    useFocusEffect(useCallback(() => {
        setScreenFocused(true);
        if (hasFocusedRef.current) refetch();
        hasFocusedRef.current = true;
        return () => setScreenFocused(false);
    }, [refetch]));

    useEffect(() => {
        const updateActiveState = (isActive) => {
            const wasActive = appActiveRef.current;
            appActiveRef.current = isActive;
            setAppActive(isActive);
            if (Platform.OS !== 'web' && isActive && !wasActive) refetch();
        };

        const subscription = AppState.addEventListener('change', (nextState) => {
            updateActiveState(nextState === 'active');
        });

        if (Platform.OS !== 'web' || typeof document === 'undefined') {
            return () => subscription.remove();
        }

        const handleVisibilityChange = () => updateActiveState(document.visibilityState === 'visible');
        document.addEventListener('visibilitychange', handleVisibilityChange);
        handleVisibilityChange();

        return () => {
            subscription.remove();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [refetch]);

    useEffect(() => {
        if (!data || selectedKey !== null) return;
        setSelectedKey(getInitialSelection(data));
    }, [data, selectedKey]);

    useEffect(() => {
        if (Platform.OS !== 'web' || typeof document === 'undefined') return;
        document.documentElement.style.backgroundColor = colors.background;
        document.body.style.backgroundColor = colors.background;
        return () => {
            document.documentElement.style.backgroundColor = '';
            document.body.style.backgroundColor = '';
        };
    }, [colors.background]);

    const effectiveSelectedKey = selectedKey ?? getInitialSelection(data);
    const selectedDate = useMemo(
        () => data?.dates.find((date) => date.key === effectiveSelectedKey),
        [data?.dates, effectiveSelectedKey],
    );
    const activeStats = effectiveSelectedKey === OVERALL_KEY ? data?.overall : selectedDate?.stats;
    const activeLabel = effectiveSelectedKey === OVERALL_KEY
        ? 'All three services'
        : `${selectedDate?.dateLabel || ''} · ${selectedDate?.label || ''}`;
    const visibleGroups = useMemo(() => METRIC_GROUPS.map((group) => ({
        ...group,
        metrics: group.metrics.filter((metric) => !metric.overallOnly || effectiveSelectedKey === OVERALL_KEY),
    })), [effectiveSelectedKey]);

    const statusText = query.isFetching
        ? 'Refreshing live totals'
        : query.isRefetchError
            ? 'Live update delayed'
            : formatUpdatedAt(data?.updatedAt, data?.event.timezone);
    const tvSelectedKey = getTvSelection(data);
    const tvSelectedDate = data?.dates.find((date) => date.key === tvSelectedKey);
    const tvShowingOverall = tvSelectedKey === OVERALL_KEY;
    const tvTodayLabel = tvShowingOverall
        ? 'All three services'
        : `${tvSelectedDate?.dateLabel || ''} · ${tvSelectedDate?.label || ''}`;
    const tvTodayStats = tvShowingOverall ? data?.overall : tvSelectedDate?.stats;

    if (isTvMode) {
        return (
            <TvMode
                data={data}
                query={query}
                effectiveScheme={effectiveScheme}
                toggleTheme={toggleTheme}
                todayLabel={tvTodayLabel}
                todayStats={tvTodayStats}
                overallStats={data?.overall}
                showingOverall={tvShowingOverall}
                insets={insets}
            />
        );
    }

    return (
        <View style={styles.page} testID="event-stats-page">
            <Stack.Screen options={{ headerShown: false, title: data?.event.name || 'Jesus Tent Revival' }} />
            <ScrollView
                contentContainerStyle={styles.scrollContent(insets.top, insets.bottom)}
                showsVerticalScrollIndicator={false}
            >
                <View style={styles.content}>
                    <View style={styles.heroOuter}>
                        <UnistylesLinearGradient
                            colors={[jtrPalette.container, colors.surface]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.hero}
                        >
                            <View style={styles.canopy} />
                            <View style={styles.heroActions}>
                                <Pressable
                                    onPress={handleRefresh}
                                    style={({ pressed }) => [styles.heroAction, pressed && styles.heroActionPressed]}
                                    accessibilityRole="button"
                                    accessibilityLabel="Refresh live totals"
                                    testID="event-stats-refresh-btn"
                                >
                                    {query.isFetching ? (
                                        <ActivityIndicator color={jtrPalette.accent} size="small" />
                                    ) : (
                                        <Unicon name="refresh-outline" size={18} color={jtrPalette.accent} />
                                    )}
                                </Pressable>
                                <Pressable
                                    onPress={toggleTheme}
                                    style={({ pressed }) => [styles.heroAction, pressed && styles.heroActionPressed]}
                                    accessibilityRole="button"
                                    accessibilityLabel={effectiveScheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                                    testID="event-stats-theme-toggle"
                                >
                                    <Unicon
                                        name={effectiveScheme === 'dark' ? 'sunny-outline' : 'moon-outline'}
                                        size={18}
                                        color={jtrPalette.accent}
                                    />
                                </Pressable>
                            </View>
                            <View style={styles.eventMark}>
                                <Unicon name="flame-outline" size={18} color={jtrPalette.accent} />
                                <Text style={styles.eyebrow}>LIVE REPORTING</Text>
                            </View>
                            <Text style={styles.heroTitle}>{data?.event.name || 'Jesus Tent Revival'}</Text>
                            <Text style={styles.heroCopy}>Excited for what God is doing!</Text>
                        </UnistylesLinearGradient>
                    </View>

                    {query.isPending && !data ? (
                        <LoadingState color={jtrPalette.accent} />
                    ) : query.isError && !data ? (
                        <ErrorState onRetry={refetch} isRetrying={query.isFetching} />
                    ) : data ? (
                        <>
                            <View style={styles.tabsSection}>
                                <ScrollView
                                    horizontal
                                    showsHorizontalScrollIndicator={false}
                                    contentContainerStyle={styles.tabs}
                                    accessibilityRole="tablist"
                                    testID="event-stats-tabs"
                                >
                                    <Pressable
                                        accessibilityRole="tab"
                                        accessibilityState={{ selected: effectiveSelectedKey === OVERALL_KEY }}
                                        aria-selected={effectiveSelectedKey === OVERALL_KEY}
                                        onPress={() => setSelectedKey(OVERALL_KEY)}
                                        style={({ pressed }) => [styles.tab, effectiveSelectedKey === OVERALL_KEY && styles.tabSelected, pressed && styles.tabPressed]}
                                        testID="event-stats-tab-overall"
                                    >
                                        <Text style={[styles.tabLabel, effectiveSelectedKey === OVERALL_KEY && styles.tabLabelSelected]}>Overall</Text>
                                        <Text style={[styles.tabDate, effectiveSelectedKey === OVERALL_KEY && styles.tabDateSelected]}>3 nights</Text>
                                    </Pressable>
                                    {data.dates.map((date) => {
                                        const isSelected = effectiveSelectedKey === date.key;
                                        return (
                                            <Pressable
                                                key={date.key}
                                                accessibilityRole="tab"
                                                accessibilityState={{ selected: isSelected }}
                                                aria-selected={isSelected}
                                                onPress={() => setSelectedKey(date.key)}
                                                style={({ pressed }) => [styles.tab, isSelected && styles.tabSelected, pressed && styles.tabPressed]}
                                                testID={`event-stats-tab-${date.label.toLowerCase()}`}
                                            >
                                                <Text style={[styles.tabLabel, isSelected && styles.tabLabelSelected]}>{date.label}</Text>
                                                <Text style={[styles.tabDate, isSelected && styles.tabDateSelected]}>{date.dateLabel}</Text>
                                            </Pressable>
                                        );
                                    })}
                                </ScrollView>
                            </View>

                            <View style={styles.totalCard} testID="event-stats-totalAttendance">
                                <View>
                                    <Text style={styles.totalLabel}>TOTAL ATTENDANCE</Text>
                                    <Text style={styles.totalContext}>{activeLabel}</Text>
                                </View>
                                <Text style={styles.totalValue}>{new Intl.NumberFormat('en-US').format(activeStats?.totalAttendance ?? 0)}</Text>
                            </View>

                            <View
                                style={[styles.liveStatus, query.isRefetchError && styles.liveStatusWarning]}
                                accessibilityLiveRegion="polite"
                                testID="event-stats-live-status"
                            >
                                {query.isFetching ? (
                                    <ActivityIndicator color={jtrPalette.accent} size="small" testID="event-stats-refreshing" />
                                ) : (
                                    <LiveDot color={query.isRefetchError ? colors.warning : colors.success} />
                                )}
                                <Text style={[styles.liveStatusText, query.isRefetchError && styles.liveStatusTextWarning]}>{statusText}</Text>
                            </View>

                            {visibleGroups.map((group) => (
                                <View key={group.title} style={styles.groupSection}>
                                    <Text style={styles.groupTitle}>{group.title}</Text>
                                    <View style={styles.metricCard}>
                                        {group.metrics.map((metric, index) => (
                                            <MetricRow
                                                key={metric.key}
                                                metric={metric}
                                                value={activeStats?.[metric.key]}
                                                isLast={index === group.metrics.length - 1}
                                            />
                                        ))}
                                    </View>
                                </View>
                            ))}

                            {effectiveSelectedKey === OVERALL_KEY && (
                                <View style={styles.groupSection} testID="event-stats-referral-sources">
                                    <Text style={styles.groupTitle}>How did you hear about this event?</Text>
                                    {data?.overall?.referralSources?.length > 0 ? (
                                        <View style={styles.metricCard}>
                                            {data.overall.referralSources.map((source, index) => (
                                                <MetricRow
                                                    key={source.label}
                                                    metric={{ key: `referral-${source.label}`, label: source.label, icon: 'megaphone-outline' }}
                                                    value={source.count}
                                                    isLast={index === data.overall.referralSources.length - 1}
                                                />
                                            ))}
                                        </View>
                                    ) : (
                                        <View style={styles.metricCard}>
                                            <Text style={styles.emptyReferralText}>No responses yet. Check back after registration opens.</Text>
                                        </View>
                                    )}
                                </View>
                            )}

                            <Text style={styles.footer}>Attendance totals only. No personal information is shown.</Text>
                        </>
                    ) : null}
                </View>
            </ScrollView>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows, spacing, roundness, typography, dark }) => {
    const jtrPalette = getJtrPalette(dark ? 'dark' : 'light');

    return ({
    page: {
        flex: 1,
        backgroundColor: colors.background,
    },
    scrollContent: (topInset, bottomInset) => ({
        flexGrow: 1,
        paddingTop: topInset + spacing.md,
        paddingBottom: bottomInset + spacing.xl,
        paddingHorizontal: spacing.md,
    }),
    content: {
        width: '100%',
        maxWidth: 680,
        alignSelf: 'center',
        gap: spacing.lg,
    },
    hero: {
        minHeight: 224,
        justifyContent: 'flex-end',
        padding: spacing.lg,
        borderRadius: roundness.xxl,
        overflow: 'hidden',
        boxShadow: shadows.card,
    },
    canopy: {
        position: 'absolute',
        width: 250,
        height: 250,
        right: -80,
        top: -150,
        borderRadius: roundness.xxl,
        backgroundColor: jtrPalette.accent,
        opacity: 0.12,
        transform: [{ rotate: '45deg' }],
    },
    heroActions: {
        position: 'absolute',
        top: spacing.sm,
        right: spacing.sm,
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        zIndex: 2,
    },
    heroAction: {
        width: 36,
        height: 36,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: roundness.md,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        _web: {
            cursor: 'pointer',
            transition: 'transform 150ms ease, opacity 150ms ease',
        },
    },
    heroActionPressed: {
        opacity: 0.7,
        transform: [{ scale: 0.94 }],
    },
    eventMark: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        marginBottom: spacing.md,
    },
    eyebrow: {
        color: jtrPalette.accent,
        fontFamily: fonts.bold,
        fontSize: typography.micro,
        letterSpacing: 1.8,
    },
    heroTitle: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: 32,
        lineHeight: 36,
        letterSpacing: -0.8,
        maxWidth: 440,
    },
    heroCopy: {
        color: colors.onSurfaceVariant,
        fontFamily: fonts.body,
        fontSize: typography.body,
        lineHeight: 22,
        marginTop: spacing.sm,
        maxWidth: 450,
    },
    stateCard: {
        minHeight: 220,
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.sm,
        padding: spacing.xl,
        borderRadius: roundness.xl,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
    },
    errorIcon: {
        width: 48,
        height: 48,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: spacing.xs,
        borderRadius: 24,
        backgroundColor: jtrPalette.container,
    },
    stateTitle: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: typography.h2,
        textAlign: 'center',
    },
    stateCopy: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        lineHeight: 19,
        textAlign: 'center',
        maxWidth: 320,
        marginBottom: spacing.sm,
    },
    tabsSection: {
        marginHorizontal: -spacing.md,
    },
    tabs: {
        gap: spacing.sm,
        paddingHorizontal: spacing.md,
    },
    tab: {
        minWidth: 88,
        minHeight: 58,
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm,
        borderRadius: roundness.md,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        _web: {
            cursor: 'pointer',
            transition: 'transform 150ms ease, background-color 150ms ease',
            outlineColor: jtrPalette.accent,
            outlineOffset: 2,
        },
    },
    tabSelected: {
        backgroundColor: jtrPalette.accent,
    },
    tabPressed: {
        opacity: 0.82,
        transform: [{ scale: 0.98 }],
    },
    tabLabel: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: typography.caption,
    },
    tabLabelSelected: {
        color: jtrPalette.onAccent,
    },
    tabDate: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.micro,
        marginTop: 2,
    },
    tabDateSelected: {
        color: jtrPalette.onAccent,
        opacity: 0.82,
    },
    totalCard: {
        minHeight: 132,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.md,
        padding: spacing.lg,
        borderRadius: roundness.xl,
        backgroundColor: jtrPalette.container,
        boxShadow: shadows.card,
    },
    totalLabel: {
        color: jtrPalette.accent,
        fontFamily: fonts.bold,
        fontSize: typography.micro,
        letterSpacing: 1.3,
    },
    totalContext: {
        color: colors.onSurfaceVariant,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        marginTop: spacing.xs,
    },
    totalValue: {
        color: jtrPalette.accent,
        fontFamily: fonts.bold,
        fontSize: 52,
        lineHeight: 58,
        letterSpacing: -2,
        fontVariant: ['tabular-nums'],
    },
    liveStatus: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        minHeight: 28,
        paddingHorizontal: spacing.sm,
        borderRadius: roundness.sm,
        backgroundColor: colors.successContainer,
        boxShadow: shadows.card,
    },
    liveStatusWarning: {
        backgroundColor: colors.warningContainer,
    },
    liveDot: {
        width: 7,
        height: 7,
        borderRadius: 4,
        backgroundColor: colors.success,
    },
    liveDotWarning: {
        backgroundColor: colors.warning,
    },
    liveStatusText: {
        color: colors.success,
        fontFamily: fonts.bold,
        fontSize: typography.micro,
    },
    liveStatusTextWarning: {
        color: colors.warning,
    },
    groupSection: {
        gap: spacing.sm,
    },
    groupTitle: {
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        fontSize: typography.micro,
        letterSpacing: 1.1,
        textTransform: 'uppercase',
        paddingHorizontal: spacing.xs,
    },
    metricCard: {
        paddingHorizontal: spacing.md,
        borderRadius: roundness.lg,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
    },
    metricRow: {
        minHeight: 68,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.md,
    },
    metricRowDivider: {
        borderBottomWidth: StyleSheet.hairlineWidth,
        borderBottomColor: colors.borderSubtle,
    },
    metricIdentity: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.md,
    },
    metricIcon: {
        width: 36,
        height: 36,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: roundness.md,
        backgroundColor: jtrPalette.container,
    },
    metricLabel: {
        flex: 1,
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: typography.body,
    },
    metricValue: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: typography.h1,
        fontVariant: ['tabular-nums'],
    },
    footer: {
        color: colors.textTertiary,
        fontFamily: fonts.body,
        fontSize: typography.micro,
        lineHeight: 16,
        textAlign: 'center',
        paddingHorizontal: spacing.lg,
    },
    emptyReferralText: {
        color: colors.textSecondary,
        fontFamily: fonts.body,
        fontSize: typography.caption,
        textAlign: 'center',
        paddingVertical: spacing.lg,
    },
    tvPage: (isDark) => ({
        flex: 1,
        backgroundColor: isDark ? '#000000' : '#FFFFFF',
        overflow: 'hidden',
    }),
    tvContent: (topInset, bottomInset) => ({
        flex: 1,
        width: '100%',
        maxWidth: 1920,
        alignSelf: 'center',
        flexDirection: 'row',
        gap: spacing.xxl,
        paddingTop: topInset + spacing.xl,
        paddingBottom: bottomInset + spacing.xl,
        paddingHorizontal: spacing.xxl,
    }),
    tvAction: {
        position: 'absolute',
        top: spacing.lg,
        right: spacing.lg,
        zIndex: 2,
        width: 48,
        height: 48,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: roundness.md,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        _web: {
            cursor: 'pointer',
            transition: 'transform 150ms ease, opacity 150ms ease',
        },
    },
    tvActionPressed: {
        opacity: 0.7,
        transform: [{ scale: 0.94 }],
    },
    tvBrandPanel: {
        width: '31%',
        maxWidth: 480,
        minWidth: 300,
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: spacing.lg,
    },
    tvLogo: {
        width: '100%',
        maxWidth: 460,
        height: 520,
        flexShrink: 1,
    },
    tvBrandFooter: {
        width: '100%',
        alignItems: 'center',
        gap: spacing.md,
    },
    tvChurchLogo: {
        width: 260,
        height: 76,
    },
    tvDateRule: {
        width: 72,
        height: 4,
        borderRadius: 2,
        backgroundColor: jtrPalette.accent,
    },
    tvEventDates: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: typography.h2,
        letterSpacing: 3.2,
        textAlign: 'center',
    },
    tvScoreboard: {
        flex: 1,
        minWidth: 0,
        gap: spacing.lg,
    },
    tvSectionHeader: {
        gap: spacing.xs,
        paddingRight: 64,
    },
    tvSectionEyebrow: {
        color: jtrPalette.accent,
        fontFamily: fonts.bold,
        fontSize: typography.caption,
        letterSpacing: 3,
    },
    tvSectionTitle: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: 34,
        lineHeight: 40,
        letterSpacing: -0.8,
    },
    tvTodayRow: {
        flex: 1,
        minHeight: 280,
        flexDirection: 'row',
        gap: spacing.lg,
    },
    tvTodayCard: {
        flex: 1,
        minWidth: 0,
        justifyContent: 'space-between',
        padding: spacing.xl,
        borderRadius: roundness.xxl,
        borderCurve: 'continuous',
        backgroundColor: colors.surface,
        boxShadow: shadows.elevated,
    },
    tvStatHeading: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.md,
    },
    tvTodayIcon: {
        width: 72,
        height: 72,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: roundness.lg,
        borderCurve: 'continuous',
        backgroundColor: jtrPalette.container,
    },
    tvTodayLabel: {
        color: jtrPalette.accent,
        fontFamily: fonts.bold,
        fontSize: 48,
        lineHeight: 40,
        letterSpacing: 1.5,
        textAlign: 'center',
    },
    tvTodayValue: {
        alignSelf: 'center',
        marginVertical: 'auto',
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: 255,
        lineHeight: 184,
        letterSpacing: -6,
        fontVariant: ['tabular-nums'],
    },
    tvOverallSection: {
        gap: spacing.sm,
    },
    tvOverallEyebrow: {
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        fontSize: typography.micro,
        letterSpacing: 2.4,
    },
    tvOverallRow: {
        flexDirection: 'row',
        gap: spacing.md,
    },
    tvOverallCard: {
        flex: 1,
        minWidth: 0,
        minHeight: 128,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: spacing.md,
        padding: spacing.lg,
        borderRadius: roundness.xl,
        borderCurve: 'continuous',
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
    },
    tvOverallIcon: {
        width: 48,
        height: 48,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: roundness.md,
        borderCurve: 'continuous',
        backgroundColor: jtrPalette.container,
    },
    tvOverallLabel: {
        flex: 1,
        color: colors.textSecondary,
        fontFamily: fonts.bold,
        fontSize: typography.caption,
        lineHeight: 18,
        letterSpacing: 1.2,
    },
    tvOverallValue: {
        color: colors.text,
        fontFamily: fonts.bold,
        fontSize: 54,
        lineHeight: 60,
        letterSpacing: -2,
        fontVariant: ['tabular-nums'],
    },
    tvLiveStatus: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        minHeight: 32,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs,
        borderRadius: roundness.sm,
        backgroundColor: colors.successContainer,
        boxShadow: shadows.card,
    },
    tvLiveStatusWarning: {
        backgroundColor: colors.warningContainer,
    },
    tvLiveStatusText: {
        color: colors.success,
        fontFamily: fonts.bold,
        fontSize: typography.caption,
    },
    tvLiveStatusTextWarning: {
        color: colors.warning,
    },
    });
});

export default JesusTentRevivalStatsPage;
